/**
 * "Assemblies matched by this customer's parts": the parts the customer was invoiced or
 * quoted before the inquiry (`GET /cases/{id}/installed-base`).
 *
 * Display only. The parts point to assemblies; they do not prove the customer has the
 * machine, so the wording never says "owns". Read when the disclosure opens.
 *
 * Kept free of React so the request shape and the wording can be tested.
 */

import { type ApiError, client } from "./client";
import type { InstalledBaseFamily, InstalledBasePart, InstalledBaseView } from "./generated";

export const installedBaseKey = (caseId: string) => ["case-installed-base", caseId];

/** The view of the case's customer; null when the route does not exist (older backend). */
export async function getInstalledBase(caseId: string): Promise<InstalledBaseView | null> {
  try {
    const { data } = await client.get<{ 200: InstalledBaseView }, unknown, true>({
      url: "/cases/{case_id}/installed-base",
      path: { case_id: caseId },
      throwOnError: true,
    });
    return data && typeof data === "object" ? data : null;
  } catch (error) {
    if ((error as ApiError | null)?.status === 404) {
      return null;
    }
    throw error;
  }
}

export const INSTALLED_BASE_NOTE =
  "Assemblies matched by parts this customer was invoiced or quoted before this inquiry. Not a machine register.";

/** The sentence over the list. */
export function installedBaseLead(view: InstalledBaseView | null | undefined): string {
  if (!view || !view.enabled) {
    return "";
  }
  if (!view.account) {
    return "Confirm the customer to see the assemblies its parts belong to.";
  }
  if (!view.available) {
    return "The parts history could not be read right now.";
  }
  if ((view.families ?? []).length === 0) {
    return "No assembly can be inferred from this customer's parts history.";
  }
  return INSTALLED_BASE_NOTE;
}

/** "Invoiced", "Quoted" or "Invoiced and quoted". */
export function partSourceLabel(src: InstalledBasePart["src"]): string {
  if (src === "both") {
    return "Invoiced and quoted";
  }
  return src === "invoice" ? "Invoiced" : "Quoted";
}

/** "and 2 variants" (empty for none). */
export function variantsText(family: Pick<InstalledBaseFamily, "variants">): string {
  const n = typeof family.variants === "number" && family.variants > 0 ? family.variants : 0;
  if (n === 0) {
    return "";
  }
  return `and ${n} ${n === 1 ? "variant" : "variants"}`;
}

/** Badge tone of a tier. */
export function tierTone(tier: InstalledBaseFamily["tier"]): "success" | "info" | "neutral" {
  if (tier === "invoiced") {
    return "success";
  }
  return tier === "strong" ? "info" : "neutral";
}
